import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { useIntegraciones } from '../hooks/useIntegraciones'
import {
  ALBARANES_POR_TANDA,
  hayChatDelBot,
  useCargarAlbaranes,
  useBorrarNota,
  useCrearNota,
  useNotasCliente,
  usePerfilCliente,
  usePerfilPorTelefono,
  type AlbaranDetalle,
  type PerfilCliente as Perfil,
} from '../hooks/usePerfilCliente'
import { FichaCliente } from '../paginas/clientes/FichaCliente'
import { useGeocodificar } from '../paginas/logistica/consultas'
import { MapaCliente } from '../paginas/logistica/MapaCliente'
import { Cargando } from './Cargando'
import { EnlaceWhatsApp } from './EnlaceWhatsApp'
import { EstadoError } from './EstadoError'
import { CabeceraModal, CLASE_BOTON, CLASE_BOTON_PRIMARIO, Insignia } from './formulario'
import { IconoCerrar, IconoConversaciones, IconoEditar, IconoFactura, IconoTelefono, IconoUbicacion } from './iconos'
import { Modal } from './Modal'
import { BarraPaginacion } from './BarraPaginacion'
import { contarPaginas, TAMANO_PAGINA, usePaginacionLocal } from './paginacion'

interface Props {
  /** Cliente a mostrar; null = cerrado (salvo que venga el teléfono). */
  contactId: string | null
  /** Alternativa: abrirlo por teléfono, desde una conversación. */
  telefono?: string | null
  onCerrar: () => void
}

const TONO_CICLO: Record<string, 'positivo' | 'neutro' | 'atencion' | 'critico'> = {
  agendado: 'positivo',
  aplazado: 'neutro',
  pendiente: 'atencion',
  baja: 'critico',
}

/**
 * Perfil 360 del cliente: ficha, ubicación, estado del ciclo, historial de
 * repartos con detalle, notas y acciones (llamar, WhatsApp, su chat). Se abre
 * desde Clientes, Confirmar agendamiento o una conversación.
 *
 * La edición no se duplica: «Editar ficha» abre la ficha de Clientes (datos,
 * precios pactados y baja) en lugar del perfil, nunca encima, y al cerrarla se
 * vuelve aquí. «Ver su chat» solo aparece si hay asistente de WhatsApp.
 */
export function PerfilCliente({ contactId, telefono = null, onCerrar }: Props) {
  const porId = usePerfilCliente(contactId)
  const porTelefono = usePerfilPorTelefono(contactId ? null : telefono)
  const consulta = contactId ? porId : porTelefono
  const [editando, setEditando] = useState(false)

  if (!contactId && !telefono) return null

  const perfil = consulta.data
  if (editando && perfil) {
    return <FichaCliente id={perfil.contactId} nombre={perfil.nombre} onCerrar={() => setEditando(false)} />
  }

  const titulo = perfil ? `Perfil de ${perfil.nombre}` : 'Perfil de cliente'
  return (
    <Modal titulo={titulo} onCerrar={onCerrar} anchoMax="max-w-2xl">
      <CabeceraModal titulo={perfil?.nombre ?? 'Perfil de cliente'} onCerrar={onCerrar} />
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {consulta.isPending ? (
          <Cargando mensaje="Cargando el perfil" />
        ) : consulta.isError ? (
          <EstadoError error={consulta.error} onReintentar={() => void consulta.refetch()} />
        ) : perfil ? (
          <Contenido perfil={perfil} onEditar={() => setEditando(true)} onCerrar={onCerrar} />
        ) : null}
      </div>
    </Modal>
  )
}

function Contenido({ perfil, onEditar, onCerrar }: { perfil: Perfil; onEditar: () => void; onCerrar: () => void }) {
  const navegar = useNavigate()
  const integraciones = useIntegraciones()
  const conChat = hayChatDelBot(integraciones.data) && Boolean(perfil.enlaceChat)

  const irAlChat = () => {
    onCerrar()
    navegar(`/conversaciones/clientes?buscar=${encodeURIComponent(perfil.enlaceChat ?? '')}`)
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-tinta-2">
          {perfil.telefono ? (
            <span className="inline-flex items-center gap-1.5">
              <IconoTelefono aria-hidden="true" className="size-3.5" />
              <span>{perfil.telefono}</span>
            </span>
          ) : (
            <span>Sin teléfono</span>
          )}
          {perfil.poblacion ? <span>{perfil.poblacion}</span> : null}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {perfil.activo ? null : <Insignia tono="critico">De baja</Insignia>}
          {perfil.estadoCiclo ? (
            <Insignia tono={TONO_CICLO[perfil.estadoCiclo] ?? 'neutro'}>{perfil.estadoCiclo}</Insignia>
          ) : null}
          {perfil.etiquetas.map((etiqueta) => (
            <Insignia key={etiqueta} tono="neutro">
              {etiqueta}
            </Insignia>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {conChat ? (
          <button type="button" className={CLASE_BOTON_PRIMARIO} onClick={irAlChat}>
            <IconoConversaciones aria-hidden="true" className="size-4" />
            Ver su chat
          </button>
        ) : null}
        {perfil.telefono ? (
          <>
            <a href={`tel:${perfil.telefono}`} className={CLASE_BOTON}>
              <IconoTelefono aria-hidden="true" className="size-4" />
              Llamar
            </a>
            <EnlaceWhatsApp telefono={perfil.telefono} etiqueta="WhatsApp" className={CLASE_BOTON} />
          </>
        ) : null}
        <button type="button" className={CLASE_BOTON} onClick={onEditar}>
          <IconoEditar aria-hidden="true" className="size-4" />
          Editar ficha
        </button>
      </div>

      <section aria-label="Ubicación">
        <Titulo icono={<IconoUbicacion aria-hidden="true" className="size-3.5" />}>Ubicación</Titulo>
        <MapaUbicacion perfil={perfil} apagada={integraciones.data?.geocodificacion?.activa === false} />
      </section>

      <dl className="flex flex-col gap-1.5 rounded-xl border border-borde p-4 text-[13px]">
        <Dato k="Dirección" v={perfil.direccion} />
        <Dato k="CP / Población" v={[perfil.codigoPostal, perfil.poblacion].filter(Boolean).join(' · ')} />
        <Dato k="Provincia" v={perfil.provincia} />
        <Dato k="Aplazamientos seguidos" v={String(perfil.aplazamientos)} />
        <Dato
          k="Esta semana"
          v={perfil.respondioSemana ? (perfil.respuestaSemana ?? 'Ha respondido') : 'No ha respondido'}
          destacar={!perfil.respondioSemana}
        />
      </dl>

      <HistorialRepartos contactId={perfil.contactId} iniciales={perfil.albaranes ?? []} />
      <NotasCliente contactId={perfil.contactId} />
    </div>
  )
}

function Titulo({ icono, children }: { icono?: ReactNode; children: ReactNode }) {
  return (
    <h3 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-tinta-apagada">
      {icono}
      {children}
    </h3>
  )
}

function Dato({ k, v, destacar = false }: { k: string; v?: string | null; destacar?: boolean }) {
  return (
    <div className="flex justify-between gap-3 border-b border-dashed border-borde-suave py-1 last:border-0">
      <dt className="text-tinta-apagada">{k}</dt>
      <dd className={`text-right ${destacar ? 'font-semibold text-atencion-tinta' : 'text-tinta'}`}>{v || '—'}</dd>
    </div>
  )
}

/** Mapa con geocodificación bajo demanda (el mismo de Logística). */
function MapaUbicacion({ perfil, apagada }: { perfil: Perfil; apagada: boolean }) {
  const geocodificar = useGeocodificar(perfil.contactId)
  return (
    <MapaCliente
      nombre={perfil.nombre}
      latitud={geocodificar.data?.latitud ?? perfil.latitud}
      longitud={geocodificar.data?.longitud ?? perfil.longitud}
      onGeocodificar={() => geocodificar.mutate()}
      geocodificando={geocodificar.isPending}
      error={geocodificar.error}
      geocodificacionApagada={apagada}
    />
  )
}

/** Historial: cada albarán es un desplegable; el último sale abierto. */
function HistorialRepartos({ contactId, iniciales }: { contactId: string; iniciales: AlbaranDetalle[] }) {
  const [extra, setExtra] = useState<AlbaranDetalle[]>([])
  const [ultimaTanda, setUltimaTanda] = useState(iniciales.length)
  const cargar = useCargarAlbaranes(contactId)
  const todos = [...iniciales, ...extra]
  // Si la última tanda vino llena, puede haber más.
  const puedeHaberMas = ultimaTanda === ALBARANES_POR_TANDA
  // Se ven de 15 en 15; «Cargar más» va al final, en la última página.
  const pag = usePaginacionLocal(todos, contactId)
  const enLaUltima = pag.pagina === pag.paginas

  const cargarMas = () =>
    cargar.mutate(
      { offset: todos.length },
      {
        onSuccess: (datos) => {
          setExtra((previos) => [...previos, ...datos.items])
          setUltimaTanda(datos.items.length)
          // A la página donde empiezan los recién cargados, para verlos.
          if (datos.items.length > 0) pag.irAPagina(contarPaginas(todos.length + 1))
        },
      },
    )

  return (
    <section aria-label="Historial de repartos">
      <Titulo icono={<IconoFactura aria-hidden="true" className="size-3.5" />}>Historial de repartos</Titulo>
      {todos.length === 0 ? (
        <p className="text-sm text-tinta-apagada">Sin repartos registrados.</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {pag.visibles.map((albaran, i) => {
            const indice = (pag.pagina - 1) * TAMANO_PAGINA + i
            return <Reparto key={`${albaran.numero ?? albaran.fecha}-${indice}`} albaran={albaran} ultimo={indice === 0} />
          })}
        </div>
      )}
      {pag.paginas > 1 ? (
        <div className="mt-2">
          <BarraPaginacion pagina={pag.pagina} paginas={pag.paginas} onCambiar={pag.irAPagina} etiqueta="Paginación del historial" />
        </div>
      ) : null}
      {puedeHaberMas && enLaUltima ? (
        <button type="button" disabled={cargar.isPending} onClick={cargarMas} className={`${CLASE_BOTON} mt-2 w-full`}>
          {cargar.isPending ? 'Cargando...' : 'Cargar más albaranes'}
        </button>
      ) : null}
      {cargar.isError ? <p className="mt-2 text-sm text-critico-tinta">No se han podido cargar más albaranes.</p> : null}
    </section>
  )
}

function Reparto({ albaran, ultimo }: { albaran: AlbaranDetalle; ultimo: boolean }) {
  const resumen = albaran.lineas[0] ? `${albaran.lineas[0].unidades}× ${albaran.lineas[0].nombre}` : 'Reparto'
  return (
    <details open={ultimo} className="overflow-hidden rounded-lg border border-borde">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 [&::-webkit-details-marker]:hidden">
        <span className="flex items-center gap-1.5 text-xs text-tinta-apagada">
          {fechaLarga(albaran.fecha)}
          {albaran.numero ? <span className="text-tinta-2">{albaran.numero}</span> : null}
          {ultimo ? <Insignia tono="positivo">Último</Insignia> : null}
        </span>
        <span className="flex-1 truncate text-right text-[13px] text-tinta">{resumen}</span>
      </summary>
      <div className="border-t border-dashed border-borde-suave px-3 py-2 text-[12.5px]">
        {albaran.lineas.length === 0 ? (
          <p className="text-tinta-apagada">Sin líneas en este albarán.</p>
        ) : (
          <>
            {albaran.lineas.map((linea, i) => (
              <div key={i} className="flex justify-between py-0.5 text-tinta-2">
                <span>
                  {linea.unidades}× {linea.nombre}
                </span>
                <span>{linea.precio > 0 ? `${(linea.precio * linea.unidades).toFixed(2)} €` : '—'}</span>
              </div>
            ))}
            <div className="mt-1 flex justify-between border-t border-dashed border-borde-suave pt-1 font-semibold text-tinta">
              <span>Total</span>
              <span>{albaran.total.toFixed(2)} €</span>
            </div>
          </>
        )}
      </div>
    </details>
  )
}

/** Notas internas: se guardan en el módulo, con fecha. */
function NotasCliente({ contactId }: { contactId: string }) {
  const notas = useNotasCliente(contactId)
  const crear = useCrearNota(contactId)
  const borrar = useBorrarNota(contactId)
  const [texto, setTexto] = useState('')
  const items = notas.data?.items ?? []
  const pagNotas = usePaginacionLocal(items, contactId)

  const guardar = () => {
    const limpio = texto.trim()
    if (!limpio) return
    crear.mutate({ texto: limpio }, { onSuccess: () => setTexto('') })
  }

  return (
    <section aria-label="Notas">
      <Titulo>Notas</Titulo>
      <div className="flex flex-col gap-2">
        <label className="sr-only" htmlFor={`nota-${contactId}`}>
          Nueva nota
        </label>
        <textarea
          id={`nota-${contactId}`}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escribe una nota para el equipo"
          rows={2}
          className="w-full resize-none rounded-lg border border-borde bg-lienzo px-3 py-2 text-[13px] text-tinta outline-none placeholder:text-tinta-apagada focus:border-acento"
        />
        <button
          type="button"
          onClick={guardar}
          disabled={crear.isPending || !texto.trim()}
          className={`${CLASE_BOTON_PRIMARIO} self-start`}
        >
          {crear.isPending ? 'Guardando...' : 'Guardar nota'}
        </button>
        {crear.isError ? <EstadoError error={crear.error} /> : null}
      </div>

      {notas.isPending ? (
        <p className="mt-3 text-sm text-tinta-apagada">Cargando notas...</p>
      ) : notas.isError ? (
        <div className="mt-3">
          <EstadoError error={notas.error} onReintentar={() => void notas.refetch()} />
        </div>
      ) : items.length === 0 ? (
        <p className="mt-3 text-sm text-tinta-apagada">Sin notas todavía.</p>
      ) : (
        <ul className="mt-3 flex flex-col gap-2">
          {pagNotas.visibles.map((nota) => (
            <li key={nota.id} className="rounded-lg border border-borde bg-lienzo px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <p className="whitespace-pre-wrap break-words text-[13px] text-tinta">{nota.texto}</p>
                <button
                  type="button"
                  onClick={() => borrar.mutate({ notaId: nota.id })}
                  aria-label="Borrar la nota"
                  className="shrink-0 text-tinta-apagada transition-colors hover:text-critico-tinta"
                >
                  <IconoCerrar aria-hidden="true" className="size-4" />
                </button>
              </div>
              <p className="mt-1 text-[11px] text-tinta-apagada">{fechaHora(nota.creadaEn)}</p>
            </li>
          ))}
        </ul>
      )}
      {pagNotas.paginas > 1 ? (
        <div className="mt-2">
          <BarraPaginacion pagina={pagNotas.pagina} paginas={pagNotas.paginas} onCambiar={pagNotas.irAPagina} etiqueta="Paginación de las notas" />
        </div>
      ) : null}
    </section>
  )
}

function fechaLarga(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
}

function fechaHora(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}
